import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Input, FormGroup, Label } from "reactstrap";
import { Form, Field } from "react-final-form";
import axios from "axios";
import {
  useDispatch,
  sendFlashMessage,
  ApiErrorMessage,
} from "@/shared/components/flash";
import TemplateButton from "@/shared/components/TemplateButton";
import AvatarDropZone from "@/shared/components/form/AvatarDropZone";
import Avatar from "@/shared/components/Avatar";
import {
  mapFormDataUploads,
  unpatchMedia,
  patchMediaURL,
} from "@/shared/helpers";
import { getAuthUser, AUTH_USER_KEY } from "@/containers/App/auth";
import { ErrorMessage } from "../ErrorMsg";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import renderCheckBoxField from "@/shared/components/form/CheckBox";

const MEDIA_FIELDS = ["avatar"];

const AccountForm = ({ organization, program, auth, user, toggle }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isAllowedToChangeEmail, setIsAllowedToChangeEmail] = useState(false);

  useEffect(() => {
    if (auth && program) {
      const loginAs = auth.loginAs.name;
      setIsAllowedToChangeEmail(
        loginAs === "Manager"
          ? program.allow_managers_to_change_email
          : program.allow_participants_to_change_email
      );
    }
  }, [auth, program]);

  const validate = (values) => {
    const errors = {};
    if (!values.first_name) errors.first_name = t("please_enter_first_name");
    if (!values.last_name) errors.last_name = t("please_enter_last_name");
    if (!values.email) {
      errors.email = t("email_is_required");
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = t("invalid_email_address");
    }
    if (values?.password || values?.password_confirmation) {
      if (values.password !== values.password_confirmation)
        errors.password_confirmation = t("password_and_confirm_password_do_not_match");
      if (values.password?.trim().length < 3)
        errors.password = t("please_enter_strong_password");
    }
    return errors;
  };

  const onSubmit = (values) => {
    values = unpatchMedia(values, MEDIA_FIELDS);
    const formData = mapFormDataUploads(values);
    formData.append("_method", "PUT");
    setLoading(true);

    axios
      .post(`/organization/${organization.id}/user/${user.id}`, formData, {
        "Content-Type": "multipart/form-data",
      })
      .then((res) => {
        const newUser = res.data.user;
        const currentUser = getAuthUser();
        currentUser.avatar = newUser.avatar ?? null;
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(currentUser));
        if (res.status === 200) window.location.reload();
      })
      .catch((error) => {
        if (error?.response?.data) {
          dispatch(sendFlashMessage(<ApiErrorMessage errors={error.response.data} />, "alert-danger"));
          setLoading(false);
        }
      });
  };

  if (!user) return t("loading");

  user = patchMediaURL(user, MEDIA_FIELDS);

  const initialValues = {
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    phone_notifications: user.phone_notifications ?? false,
  };

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
  const phoneDisplay = user?.phone
    ? `${user.country_code ?? ""} ${user.phone}`.trim()
    : null;

  return (
    <div className="acct-form">
      <Form onSubmit={onSubmit} initialValues={initialValues} validate={validate}>
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>

            {/* ── Avatar header ── */}
            <div className="acct-form__avatar-section">
              <div className="acct-form__avatar-wrap">
                <Avatar
                  src={user?.avatar}
                  name={fullName}
                  className="acct-form__avatar-img"
                  fallbackClassName="acct-form__avatar-placeholder"
                />
                <div className="acct-form__avatar-dropzone">
                  <Field
                    name="avatar"
                    component={AvatarDropZone}
                    multiple={false}
                    customHeight
                    uploadTitle={{ type: "short", displayAlways: true }}
                  />
                </div>
              </div>
              <div className="acct-form__avatar-meta">
                <p className="acct-form__avatar-name">{fullName || "—"}</p>
                <p className="acct-form__avatar-email">{user.email}</p>
              </div>
            </div>

            {/* ── Personal info ── */}
            <div className="acct-form__section">
              <h3 className="acct-form__section-title">{t("account_information")}</h3>

              <div className="acct-form__row">
                <Field name="first_name">
                  {({ input, meta }) => (
                    <FormGroup className="acct-form__field">
                      <Label className="acct-form__label">* {t("first_name")}</Label>
                      <Input type="text" {...input} />
                      {meta.touched && meta.error && <ErrorMessage msg={meta.error} />}
                    </FormGroup>
                  )}
                </Field>

                <Field name="last_name">
                  {({ input, meta }) => (
                    <FormGroup className="acct-form__field">
                      <Label className="acct-form__label">* {t("last_name")}</Label>
                      <Input type="text" {...input} />
                      {meta.touched && meta.error && <ErrorMessage msg={meta.error} />}
                    </FormGroup>
                  )}
                </Field>
              </div>

              <Field name="email">
                {({ input, meta }) => (
                  <FormGroup className="acct-form__field">
                    <Label className="acct-form__label">* {t("email_address")}</Label>
                    <Input type="email" readOnly={!isAllowedToChangeEmail} {...input} />
                    {meta.touched && meta.error && <ErrorMessage msg={meta.error} />}
                  </FormGroup>
                )}
              </Field>
            </div>

            {/* ── Security ── */}
            <div className="acct-form__section">
              <h3 className="acct-form__section-title">{t("password")}</h3>
              <p className="acct-form__section-hint">Leave blank to keep your current password.</p>

              <div className="acct-form__row">
                <Field name="password">
                  {({ input, meta }) => (
                    <FormGroup className="acct-form__field">
                      <Label className="acct-form__label">{t("password")}</Label>
                      <Input type="password" placeholder="New password" {...input} />
                      {meta.touched && meta.error && <ErrorMessage msg={meta.error} />}
                    </FormGroup>
                  )}
                </Field>

                <Field name="password_confirmation">
                  {({ input, meta }) => (
                    <FormGroup className="acct-form__field">
                      <Label className="acct-form__label">{t("confirm_password")}</Label>
                      <Input type="password" placeholder="Confirm new password" {...input} />
                      {meta.touched && meta.error && <ErrorMessage msg={meta.error} />}
                    </FormGroup>
                  )}
                </Field>
              </div>
            </div>

            {/* ── Contact ── */}
            <div className="acct-form__section">
              <h3 className="acct-form__section-title">Contact & Notifications</h3>

              <FormGroup className="acct-form__field">
                <Label className="acct-form__label">Phone Number</Label>
                <div className="acct-form__phone">
                  {phoneDisplay ? (
                    <>
                      <span className="acct-form__phone-number">{phoneDisplay}</span>
                      <Link className="acct-form__phone-link" onClick={toggle}>Edit preferences</Link>
                    </>
                  ) : (
                    <Link className="acct-form__phone-link" onClick={toggle}>+ Add phone number</Link>
                  )}
                </div>
              </FormGroup>

              <div className="acct-form__checkbox-row">
                <Field
                  name="phone_notifications"
                  type="checkbox"
                  component={renderCheckBoxField}
                  label="Opt out of phone notifications"
                />
              </div>
            </div>

            {/* ── Save ── */}
            <div className="acct-form__footer">
              <TemplateButton
                type="submit"
                disabled={loading}
                text={t("save_my_account_information")}
              />
            </div>

          </form>
        )}
      </Form>
    </div>
  );
};

export default connect((state) => ({
  auth: state.auth,
  program: state.program,
  organization: state.organization,
}))(AccountForm);
